import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { palette, shadows, shape, studioBrand, typography } from "./tokens";

const heading = { fontFamily: typography.headingFamily, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 };

/** Everything but the colours: shared by the app's theme and the public site's. */
const base = {
  spacing: 8,
  shape: { borderRadius: shape.radius },
  typography: {
    fontFamily: typography.fontFamily,
    h1: { ...heading, fontSize: "2.5rem", letterSpacing: "-0.03em" },
    h2: { ...heading, fontSize: "1.875rem", letterSpacing: "-0.025em" },
    h3: { ...heading, fontSize: "1.5rem" },
    h4: { ...heading, fontSize: "1.25rem", fontWeight: 600 },
    h5: { ...heading, fontSize: "1.125rem", fontWeight: 600 },
    h6: { ...heading, fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 500 },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.55 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    overline: { fontWeight: 600, letterSpacing: "0.08em" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@font-face": [
          { fontFamily: "Davnai Editorial", src: 'url("/motion-fonts/playfair-display-latin-700-normal.woff2") format("woff2")', fontWeight: 700, fontStyle: "normal", fontDisplay: "swap" },
          // The landing page's accent phrase ("ready to publish.").
          { fontFamily: "Davnai Accent", src: 'url("/motion-fonts/instrument-serif-latin-400-italic.woff2") format("woff2")', fontWeight: 400, fontStyle: "italic", fontDisplay: "swap" },
        ],
        "a:focus-visible, button:focus-visible": { outline: `2px solid ${studioBrand.focus}`, outlineOffset: 3 },
        "@media (prefers-reduced-motion: reduce)": { "*, *::before, *::after": { animationDuration: "0.01ms !important", transitionDuration: "0.01ms !important" } },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // New look: easy to tap (40 px, 48 large), rounder corners.
        root: ({ theme }) => ({ paddingInline: 18, borderRadius: shape.controlRadius, minHeight: 40,
          "&.MuiButton-contained.MuiButton-colorPrimary": {
            backgroundColor: studioBrand.action,
            color: studioBrand.onAction,
            "&:hover": { backgroundColor: studioBrand.actionHover },
            "&.Mui-disabled": { backgroundColor: (theme.vars ?? theme).palette.action.disabledBackground, color: (theme.vars ?? theme).palette.action.disabled },
          },
        }),
        sizeSmall: { minHeight: 32, paddingInline: 12 },
        sizeLarge: { paddingBlock: 11, paddingInline: 24, fontSize: "1rem", minHeight: 48 },
      },
    },
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: shape.cardRadius } },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        // New look: a hairline border and no shadow at rest (hover states add their own).
        root: ({ theme }) => ({
          boxShadow: "none",
          transition: "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
          ...theme.applyStyles("dark", { boxShadow: "none" }),
        }),
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.vars?.palette.divider ?? theme.palette.divider}`,
          backgroundColor: theme.vars?.palette.background.paper ?? theme.palette.background.paper,
        }),
      },
    },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: shape.controlRadius } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: shape.radius } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 20 } } },
    MuiMenu: { styleOverrides: { paper: { borderRadius: 12, boxShadow: shadows.card } } },
    // New look: every set of tabs is one grey pill bar; the chosen tab is a white raised pill (approved "Davnai New Look").
    MuiTabs: {
      defaultProps: { variant: "scrollable", scrollButtons: false },
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 44,
          padding: 4,
          borderRadius: 12,
          display: "inline-flex",
          maxWidth: "100%",
          verticalAlign: "middle",
          backgroundColor: theme.vars?.palette.action.hover ?? theme.palette.action.hover,
        }),
        indicator: { display: "none" },
        list: { gap: 4 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 600,
          minHeight: 36,
          minWidth: 0,
          padding: "0 14px",
          borderRadius: 9,
          fontSize: 14,
          color: theme.vars?.palette.text.secondary ?? theme.palette.text.secondary,
          "&.Mui-selected": {
            color: theme.vars?.palette.text.primary ?? theme.palette.text.primary,
            fontWeight: 700,
            backgroundColor: theme.vars?.palette.background.paper ?? theme.palette.background.paper,
            boxShadow: shadows.tab,
          },
        }),
      },
    },
  },
} satisfies ThemeOptions;

/** The app: light or dark, as chosen in Settings → Appearance (default: follow the device). */
export const theme = createTheme({
  ...base,
  cssVariables: { colorSchemeSelector: "class" },
  colorSchemes: {
    light: { palette: palette.light },
    dark: { palette: palette.dark },
  },
});
